import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService, registerService, healthService, operatorService, databaseService, API_BASE_URL } from '@/services/api'
import router from '@/router'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(JSON.parse(localStorage.getItem('user') || 'null'))
  const token = ref(localStorage.getItem('token'))
  const preAuthToken = ref(localStorage.getItem('preAuthToken') || null)
  const selectedRole = ref(localStorage.getItem('selectedRole') || null)
  const companies = ref(JSON.parse(localStorage.getItem('companies') || '[]'))
  const selectedCompany = ref(JSON.parse(localStorage.getItem('selectedCompany') || 'null'))
  const permissions = ref(JSON.parse(localStorage.getItem('permissions') || '[]'))
  
  // New: Store intended company from landing page
  const intendedCompany = ref(localStorage.getItem('intendedCompany') || null)
  
  // Load operator status from localStorage
  const isOperator = ref(localStorage.getItem('isOperator') === 'true')
  const isPeak = ref(localStorage.getItem('isPeak') === 'true')
  const operatorData = ref(JSON.parse(localStorage.getItem('operatorData') || 'null'))
  const operatorCheckComplete = ref(localStorage.getItem('operatorCheckComplete') === 'true')
  
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const redirectPath = ref(null)
  const checkingOperator = ref(false)

  const companyDatabases = ref(JSON.parse(localStorage.getItem('companyDatabases') || '[]'))
  const activeDatabase = ref(localStorage.getItem('activeDatabase') || 'db1')
  const switchingDatabase = ref(false)

  const login = async (phoneNumber, password) => {
    try {
      const response = await authService.login(phoneNumber, password)
      
      console.log('Login responseeeeeeeeeeeeeeeee:', response)
      
      if (response.data.success) {
        // Get data from response
        const { 
          access_token, 
          subcode, 
          customer_id, 
          roles,
          primary_role,
          first_name,
          last_name,
          email,
          companies: responseCompanies,
          selected_company
        } = response.data
        
        // Store token - this is the current token (could be pre-auth or final)
        token.value = access_token
        localStorage.setItem('token', token.value)
        
        // IMPORTANT: Check if this is a pre-auth token (multiple companies but no selected company)
        const isPreAuth = responseCompanies && responseCompanies.length > 1 && !selected_company
        
        if (isPreAuth) {
          // Store pre-auth token separately for company selection
          localStorage.setItem('preAuthToken', access_token)
          preAuthToken.value = access_token
          console.log('Pre-auth token stored for company selection')
        } else {
          // Clear pre-auth token if not needed
          localStorage.removeItem('preAuthToken')
          preAuthToken.value = null
        }
        
        // Create user object
        const userU = {
          id: customer_id,
          customerId: customer_id,
          phone: phoneNumber,
          name: first_name && last_name 
            ? `${first_name} ${last_name}`
            : first_name || last_name || '',
          firstName: first_name || '',
          lastName: last_name || '',
          email: email || '',
          isVerified: true,
          subCodeId: subcode,
          roles: roles || [],
          primaryRole: primary_role || (roles && roles[0]) || 'customer'
        }

        console.log('User data with roles:', userU)
        user.value = userU
        localStorage.setItem('user', JSON.stringify(userU))
        
        // Clear selected role on new login
        selectedRole.value = null
        localStorage.removeItem('selectedRole')
        
        // Store companies if provided
        if (responseCompanies) {
          companies.value = responseCompanies
          localStorage.setItem('companies', JSON.stringify(responseCompanies))
        }
        
        // Store selected company if provided (auto-selected for single company)
        if (selected_company) {
          selectedCompany.value = selected_company
          localStorage.setItem('selectedCompany', JSON.stringify(selected_company))
        }
        
        // Return login result with additional info for auto-login
        return {
          success: true,
          loginData: response.data,
          isPreAuth,
          hasMultipleRoles: roles && roles.length > 1,
          hasMultipleCompanies: responseCompanies && responseCompanies.length > 1,
          hasSelectedCompany: !!selected_company,
          companies: responseCompanies
        }
      } else {
        throw new Error(response.data.message)
      }
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  const fetchCompanyDatabases = async () => {
    try {
      const response = await databaseService.getCompanyDatabases()

      if (response.data.success) {
        companyDatabases.value = response.data.databases || []
        activeDatabase.value = response.data.active_db || 'db1'

        localStorage.setItem('companyDatabases', JSON.stringify(companyDatabases.value))
        localStorage.setItem('activeDatabase', activeDatabase.value)

        return response.data
      }

      throw new Error(response.data.message || 'Failed to load databases')
    } catch (error) {
      console.error('Fetch company databases failed:', error)
      companyDatabases.value = []
      localStorage.removeItem('companyDatabases')
      throw error
    }
  }

  const switchDatabase = async (targetDb) => {
    if (!targetDb) return

    if (targetDb === activeDatabase.value) {
      return {
        success: true,
        alreadyActive: true
      }
    }

    switchingDatabase.value = true

    try {
      const response = await databaseService.switchDatabase(targetDb)

      console.log('response ', response)

      if (response.data.success) {
        const newToken = response.data.access_token

        token.value = newToken
        localStorage.setItem('token', newToken)

        activeDatabase.value = response.data.active_db || targetDb
        localStorage.setItem('activeDatabase', activeDatabase.value)

        companyDatabases.value = companyDatabases.value.map(db => ({
          ...db,
          is_active: db.key === activeDatabase.value
        }))

        localStorage.setItem('companyDatabases', JSON.stringify(companyDatabases.value))

        return response.data
      }

      throw new Error(response.data.message || 'Database switch failed')

    } catch (error) {
      console.error('Database switch failed:', error)
      alert(error.response?.data?.detail || 'خطا در تغییر پایگاه داده')
      throw error
    } finally {
      switchingDatabase.value = false
    }
  }

  // NEW: Method to handle auto-login after company landing page
  const handleIntendedCompany = async () => {
    const intendedCompanyId = localStorage.getItem('intendedCompany')
    
    if (!intendedCompanyId) {
      return { autoSelected: false, reason: 'no_intended_company' }
    }
    
    // Clear intended company from storage
    localStorage.removeItem('intendedCompany')
    intendedCompany.value = null
    
    // Check if user has access to this company
    const hasAccess = companies.value?.some(
      company => company.company_id == intendedCompanyId
    )
    
    if (!hasAccess) {
      return { autoSelected: false, reason: 'no_access' }
    }
    
    // Check if company has DB access
    const company = companies.value.find(c => c.company_id == intendedCompanyId)
    if (!company?.has_db_access) {
      return { autoSelected: false, reason: 'no_db_access' }
    }
    
    try {
      // Auto-select the company
      const result = await selectCompany(intendedCompanyId)
      return { 
        autoSelected: true, 
        company,
        result 
      }
    } catch (error) {
      console.error('Auto-select company failed:', error)
      return { autoSelected: false, reason: 'selection_failed', error }
    }
  }

  // NEW: Method to set intended company (called from landing page)
  const setIntendedCompany = (companyId) => {
    intendedCompany.value = companyId
    localStorage.setItem('intendedCompany', companyId)
  }

  // NEW: Method to check if user has access to a specific company
  const hasCompanyAccess = (companyId) => {
    return companies.value?.some(company => company.company_id == companyId) || false
  }

  // NEW: Method to get company by ID
  const getCompanyById = (companyId) => {
    return companies.value?.find(company => company.company_id == companyId) || null
  }

  // Add method to get pre-auth token for company switching
  const getPreAuthToken = () => {
    return preAuthToken.value
  }

  // Add method to switch company (uses stored pre-auth token)
const switchStore = async (storeId) => {
  try {
    // Use the select-store endpoint with the current token
    const response = await authService.selectStoreWithToken(storeId, preAuthToken.value)
    
    console.log('Switch store response:', response.data)
    
    if (response.data.success) {
      const { 
        access_token, 
        subcode, 
        customer_id, 
        roles,
        permissions: responsePermissions,
        primary_role,
        first_name,
        last_name,
        email,
        companies: responseCompanies,
        selected_company
      } = response.data
      
      // Update token with the new token from response
      token.value = access_token
      localStorage.setItem('token', token.value)
      
      // Update user object
      const userU = {
        id: customer_id,
        customerId: customer_id,
        phone: user.value?.phone || '',
        name: first_name && last_name 
          ? `${first_name} ${last_name}`
          : first_name || last_name || '',
        firstName: first_name || '',
        lastName: last_name || '',
        email: email || '',
        isVerified: true,
        subCodeId: subcode,
        roles: roles || [],
        primaryRole: primary_role || (roles && roles[0]) || 'store'
      }

      user.value = userU
      localStorage.setItem('user', JSON.stringify(userU))
      
      selectedRole.value = null
      localStorage.removeItem('selectedRole')
      
      // Update selected company with db_accessible flag
      if (selected_company) {
        const companyWithAccess = {
          ...selected_company,
          db_accessible: selected_company.db_accessible !== undefined 
            ? selected_company.db_accessible 
            : true
        } 
        selectedCompany.value = companyWithAccess
        localStorage.setItem('selectedCompany', JSON.stringify(companyWithAccess))
        console.log('Selected store db_accessible:', companyWithAccess.db_accessible)
      }
      
      // Update companies list
      if (responseCompanies) {
        companies.value = responseCompanies
        localStorage.setItem('companies', JSON.stringify(responseCompanies))
      }
      
      // Update permissions
      permissions.value = responsePermissions || []
      localStorage.setItem('permissions', JSON.stringify(permissions.value))
      
      // Set store role
      setSelectedRole('store')
      
      return {
        success: true,
        data: response.data
      }
    } else {
      throw new Error(response.data.message || 'Store switch failed')
    }
  } catch (error) {
    console.error('Store switch failed:', error)
    throw error
  }
}

const switchCompany = async (companyId, companyType = 'private') => {
  try {
    // Check if this is a store selection
    if (companyType === 'public_store' || companyType === 'private_store') {
      return await switchStore(companyId)
    }
    
    // Use the select-company endpoint with the current token
    const response = await authService.selectCompanyWithToken(companyId, token.value)
    
    console.log('Switch company response:', response.data)
    
    if (response.data.success) {
      const { 
        access_token, 
        subcode, 
        customer_id, 
        roles,
        permissions: responsePermissions,
        primary_role,
        first_name,
        last_name,
        email,
        companies: responseCompanies,
        selected_company
      } = response.data
      
      // Update token with the new token from response
      token.value = access_token
      localStorage.setItem('token', token.value)
      
      // Update user object
      const userU = {
        id: customer_id,
        customerId: customer_id,
        phone: user.value?.phone || '',
        name: first_name && last_name 
          ? `${first_name} ${last_name}`
          : first_name || last_name || '',
        firstName: first_name || '',
        lastName: last_name || '',
        email: email || '',
        isVerified: true,
        subCodeId: subcode,
        roles: roles || [],
        primaryRole: primary_role || (roles && roles[0]) || 'customer'
      }

      user.value = userU
      localStorage.setItem('user', JSON.stringify(userU))
      
      selectedRole.value = null
      localStorage.removeItem('selectedRole')
      
      // Update selected company with db_accessible flag
      if (selected_company) {
        const companyWithAccess = {
          ...selected_company,
          db_accessible: selected_company.db_accessible !== undefined 
            ? selected_company.db_accessible 
            : true
        } 
        selectedCompany.value = companyWithAccess
        localStorage.setItem('selectedCompany', JSON.stringify(companyWithAccess))
        console.log('Selected company db_accessible:', companyWithAccess.db_accessible)
      }
      
      // Update companies list
      if (responseCompanies) {
        companies.value = responseCompanies
        localStorage.setItem('companies', JSON.stringify(responseCompanies))
      }
      
      // Update permissions
      permissions.value = responsePermissions || []
      localStorage.setItem('permissions', JSON.stringify(permissions.value))

      localStorage.removeItem('latestTarazAnalysis')
      
      return {
        success: true,
        data: response.data
      }
    } else {
      throw new Error(response.data.message || 'Company switch failed')
    }
  } catch (error) {
    console.error('Company switch failed:', error)
    throw error
  }
}

  // Method to select a company after login
  const selectCompany = async (companyId, companyName = '', selectedSubcode = null) => {
    try {
      isPeak.value = false
      localStorage.removeItem('isPeak')
      // Determine which token to use
      let tokenToUse = token.value
      
      // If we have a pre-auth token and this is a company selection scenario
      const hasMultipleCompanies = companies.value?.length > 1 && !selectedCompany.value
      const preAuthTokenFromStorage = localStorage.getItem('preAuthToken')
      
      if (hasMultipleCompanies && preAuthTokenFromStorage) {
        // Use pre-auth token for company selection
        tokenToUse = preAuthTokenFromStorage
        console.log('Using pre-auth token for company selection')
      }
      
      if (!tokenToUse) {
        console.error('No token found, redirecting to login')
        alert('no token')
        window.location.href = '/login'
        throw new Error('No authentication token found')
      }
      
      console.log('Selecting company with token:', tokenToUse.substring(0, 20) + '...')
      
      // Temporarily set the token for this request
      const originalToken = localStorage.getItem('token')
      localStorage.setItem('token', tokenToUse)
      
      const response = await authService.selectCompany(companyId, selectedSubcode)
      try {
        console.log('Select company response:', response.data)

        if (response.data?.requires_parent_selection) {
          localStorage.setItem('token', originalToken || '')
          return {
            success: false,
            requires_parent_selection: true,
            parent_matches: response.data.parent_matches || [],
          }
        }
        
        if (response.data.success) {
          const { 
            access_token, 
            subcode, 
            customer_id, 
            roles,
            permissions: responsePermissions,
            primary_role,
            first_name,
            last_name,
            email,
            companies: responseCompanies,
            selected_company
          } = response.data
          
          // Update token with the new token from response
          token.value = access_token
          localStorage.setItem('token', token.value)
          console.log(responseCompanies)        
          // Update user object
          const userU = {
            id: customer_id,
            customerId: customer_id,
            phone: user.value?.phone || '',
            name: first_name && last_name 
              ? `${first_name} ${last_name}`
              : first_name || last_name || '',
            firstName: first_name || '',
            lastName: last_name || '',
            email: email || '',
            isVerified: true,
            subCodeId: subcode,
            roles: roles || [],
            primaryRole: primary_role || (roles && roles[0]) || 'customer'
          }

          user.value = userU
          localStorage.setItem('user', JSON.stringify(userU))
          
          selectedRole.value = null
          localStorage.removeItem('selectedRole')
          
          // Update selected company with db_accessible flag
          if (selected_company) {
            // Ensure db_accessible is set (default to true if not present)
            const companyWithAccess = {
              ...selected_company,
              db_accessible: selected_company.db_accessible !== undefined 
                ? selected_company.db_accessible 
                : true
            } 
            selectedCompany.value = companyWithAccess
            localStorage.setItem('selectedCompany', JSON.stringify(companyWithAccess))
            console.log('Selected company db_accessible:', companyWithAccess.db_accessible)
          }
          
          // Clear intended company if this was an auto-select
          if (intendedCompany.value) {
            intendedCompany.value = null
            localStorage.removeItem('intendedCompany')
          }
          
          // Check if database is accessible and redirect accordingly
          const isDbAccessible = selected_company?.db_accessible !== false
          const isViewer = roles.includes('VIEWER')

          permissions.value = responsePermissions || []
          localStorage.setItem('permissions', JSON.stringify(permissions.value))

          
          console.log(isDbAccessible)
          if (isDbAccessible || isViewer) {
              router.push('/select-role')
          }
          if (isDbAccessible) {
            // Database accessible - go to role selection
            if (roles && roles.length > 0) {
              //alert('select-role')
              router.push('/select-role')
            } else {
              //alert('dashboard')
              router.push('/select-role')
            }
          } else {
            //alert('not accessible')
            // Database not accessible - go to company landing page
            console.log('Database not accessible, redirecting to company landing page')
            router.push('/company/' + encodeURIComponent(selected_company.company_name))
          }
          
          await checkOperatorStatus()
          
          return {
            success: true,
            data: response.data
          }
        } else {
          throw new Error(response.data.message || 'Company selection failed')
        }
      } finally {
        // Restore original token if needed (though we've updated it)
        if (!response?.data?.success) {
          localStorage.setItem('token', originalToken)
        }
      }
    } catch (error) {
      console.error('Company selection failed:', error)
      
      // Get custom headers from error response
      const targetCompanyId = error.response?.headers?.['x-target-company-id']
      const targetCompanyName = error.response?.headers?.['x-target-company-name']
      const dbConnectionError = error.response?.headers?.['x-db-connection-error']
      
      console.log('Target company ID:', targetCompanyId)
      console.log('Target company name:', decodeURIComponent(targetCompanyName || ''))
      console.log('DB connection error:', dbConnectionError)
      
      // Handle based on status code
      if (error.response?.status === 503 || error.response?.status === 500) {
          // Database connection error - show message and maybe redirect to landing page
          alert('خطا در اتصال به پایگاه داده شرکت. لطفاً بعداً تلاش کنید.')
          
          if (targetCompanyId && targetCompanyName) {
              // Redirect to company landing page
              router.push(`/company/${encodeURIComponent(targetCompanyName)}`)
              logout()
          }
      } else if (error.response?.status === 406) {
        alert('شما در شرکت انتخاب شده احراز هویت نکردید!')
        router.push({
          path: '/register-company-phone',
          query: { 
            companyId: companyId,
            companyName: companyName
          }
        })
      } else if (error.response?.status === 403) {
          alert('کاربر در پایگاه داده شرکت مورد نظر یافت نشد.')
      } else {
          alert(error.response?.data?.detail || 'خطا در انتخاب شرکت')
      }
    }
  }

  // Method to check operator status
  const checkOperatorStatus = async () => {
    checkingOperator.value = true
    try {
      const response = await operatorService.checkIfOperator()
      console.log('Operator check response:', response)
      
      if (response.data?.success) {
        isOperator.value = response.data.is_operator
        operatorData.value = response.data.is_operator ? response.data.operator_data : null
        
        // Save to localStorage
        localStorage.setItem('isOperator', String(response.data.is_operator))
        if (response.data.is_operator && response.data.operator_data) {
          localStorage.setItem('operatorData', JSON.stringify(response.data.operator_data))
        } else {
          localStorage.removeItem('operatorData')
        }
      } else {
        isOperator.value = false
        operatorData.value = null
        localStorage.removeItem('isOperator')
        localStorage.removeItem('operatorData')
      }
    } catch (error) {
      console.error('Error checking operator status:', error)
      isOperator.value = false
      operatorData.value = null
      localStorage.removeItem('isOperator')
      localStorage.removeItem('operatorData')
    } finally {
      checkingOperator.value = false
      operatorCheckComplete.value = true
      localStorage.setItem('operatorCheckComplete', 'true')
    }
  }

  const setIsPeak = () => {
    isPeak.value = true
    localStorage.setItem('isPeak', true)
  }

  // Method to set selected role for users with multiple roles
  const setSelectedRole = (role) => {
    selectedRole.value = role
    localStorage.setItem('selectedRole', role)
    
    // Update user's current role
    if (user.value) {
      user.value.currentRole = role
      localStorage.setItem('user', JSON.stringify(user.value))
    }
  }

  // Method to set selected company
  const setSelectedCompany = (company) => {
    selectedCompany.value = company
    localStorage.setItem('selectedCompany', JSON.stringify(company))
  }

  // Get current active role (selected role for multi-role users, primary role for others)
  const getCurrentRole = computed(() => {
    if (selectedRole.value) {
      return selectedRole.value
    }
    return user.value?.primaryRole || user.value?.roles?.[0] || 'customer'
  })

  // Check if user has specific role
  const hasRole = (role) => {
    return user.value?.roles?.includes(role) || false
  }

  // Check if user has multiple roles
  const hasMultipleRoles = computed(() => {
    return (user.value?.roles?.length || 0) > 1 || false
  })

  // Get available roles for user (synchronous computed property)
  const getAvailableRoles = computed(() => {
    const roles = user.value?.roles ? [...user.value.roles] : [];
    
    // Always add sales role (your existing logic)
    // if (!roles.includes("sales")) {
    //   roles.push("sales");
    // }
    
    // Add operator role if user is operator (using reactive isOperator)
    if (isOperator.value) {
      if (!roles.includes("operator")) {
        roles.push("operator");
      }
    }

    if (isPeak.value) {
      if (!roles.includes("driver")) {
        roles.push("driver");
        roles.push("requester");
      }
    }

    if (!roles.includes("office")) {
      roles.push("office");
    }
    
    // For now each user who is employer is also has role system_admin 
    // if (roles.includes("employer")) {
    //   if (!roles.includes("system_admin")) {
    //     roles.push("system_admin");
    //   }
    // }
    
    //console.log("Available roles ", roles)
    //alert('roless ' + roles)
    return roles;
  });

  // Check if user has multiple companies
  const hasMultipleCompanies = computed(() => {
    return companies.value?.length > 1 || false
  })

  // Check if user has selected a company
  const hasCompanySelected = computed(() => {
    return !!selectedCompany.value
  })

  // Get companies with DB access
  const getCompaniesWithDbAccess = computed(() => {
    return companies.value.filter(company => company.has_db_access)
  })

  // Get the first company with DB access
  const getFirstAccessibleCompany = computed(() => {
    return getCompaniesWithDbAccess.value[0]
  })

  const logout = async () => {
    console.log('logout')

    try {
      await authService.logout()
    } finally {
      token.value = null
      user.value = null
      selectedRole.value = null
      companies.value = []
      selectedCompany.value = null
      isOperator.value = false
      operatorData.value = null
      operatorCheckComplete.value = false
      checkingOperator.value = false
      intendedCompany.value = null
      preAuthToken.value = null
      isPeak.value = false
      permissions.value = []
      companyDatabases.value = []
      activeDatabase.value = 'db1'
      switchingDatabase.value = false

      localStorage.removeItem('companyDatabases')
      localStorage.removeItem('activeDatabase')
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('selectedRole')
      localStorage.removeItem('companies')
      localStorage.removeItem('selectedCompany')
      localStorage.removeItem('isOperator')
      localStorage.removeItem('operatorData')
      localStorage.removeItem('operatorCheckComplete')
      localStorage.removeItem('intendedCompany')
      localStorage.removeItem('preAuthToken')
      localStorage.removeItem('isPeak')
      localStorage.removeItem('permissions')
      localStorage.removeItem('accountant_customer_id')
      localStorage.removeItem('accountant_operator_id')
      localStorage.removeItem('latestTarazAnalysis')
    }
  }


  const setPasswordAndLogin = async (phoneNumber, password) => {
    try {
      const response = await registerService.setPassword(phoneNumber, password)
      
      console.log('Set password response:', response.data)
      
      if (response.data.success) {
        const { 
          access_token, 
          subcode, 
          customer_id,
          companies: responseCompanies,
          selected_company,
          updated_companies
        } = response.data
        
        const userU = {
          id: customer_id,
          customerId: customer_id,
          phone: phoneNumber,
          name: '',
          firstName: '',
          lastName: '',
          email: '',
          isVerified: true,
          subCodeId: subcode,
          roles: ['customer'],
          primaryRole: 'customer'
        }
        
        console.log('User data after set password:', userU)
        
        token.value = access_token
        user.value = userU
        
        localStorage.setItem('token', token.value)
        localStorage.setItem('user', JSON.stringify(userU))
        
        // Store companies if provided
        if (responseCompanies) {
          companies.value = responseCompanies
          localStorage.setItem('companies', JSON.stringify(responseCompanies))
        }
        
        // Store selected company if provided
        if (selected_company) {
          selectedCompany.value = selected_company
          localStorage.setItem('selectedCompany', JSON.stringify(selected_company))
        }
        
        // Check operator status after setting password
        await checkOperatorStatus()
        
        return {
          ...response,
          updatedCompanies: updated_companies
        }
      } else {
        throw new Error(response.data.message)
      }
    } catch (error) {
      console.error('Set password failed:', error)
      throw error
    }
  }

  const checkAuth = async () => {
    if (!user.value || !token.value) {
      console.warn('No user or token found')
      return false
    }

    try {
      await healthService.check()
      console.log('Token is valid')
      return true
    } catch (error) {
      console.error('Token validation failed:', error)
      
      if (error.response?.status === 401) {
        await logout()
      }
      return false
    }
  }

  // Helper to get subcode from user data
  const getSubcode = computed(() => {
    return user.value?.subCodeId || null
  })

  // Helper to get customer ID
  const getCustomerId = computed(() => {
    return user.value?.customerId || user.value?.id
  })

  // Helper to get selected company ID
  const getSelectedCompanyId = computed(() => {
    return selectedCompany.value?.company_id
  })

  const needsCompanySelection = computed(() => {
    // If user is authenticated but has no company selected AND has companies
    return isAuthenticated.value && 
          !hasCompanySelected.value && 
          companies.value.length > 0
  })

  const getRoleDashboard = (role) => {
    switch(role) {
      case 'customer': 
        return '/dashboard'
      case 'employer': 
        return '/employer/dashboard'
      case 'admin': 
        return '/admin/dashboard'
      case 'store': 
        return '/sales/kala-store'
      case 'operator': {
        // Add curly braces to create a block scope
        const companyId = getSelectedCompanyId.value
        const companyName = selectedCompany.value?.company_name || ''
        return {
          path: '/operator-panel',
          query: {
            department: 'support',
            companyId: companyId,
            companyName: companyName
          }
        }
      }
      case 'driver': 
        return '/peak/driver'
      case 'requester': 
        return '/peak/register-driver'
      case 'office': 
        return '/office/dashboard'
      default: 
        return '/dashboard'
    }
  }

  // Initialize: Load operator status from localStorage on store creation
  const initializeFromStorage = () => {
    // This is automatically handled by the ref initial values
    console.log('Auth store initialized with:', {
      isOperator: isOperator.value,
      operatorData: operatorData.value,
      operatorCheckComplete: operatorCheckComplete.value,
      //intendedCompany: intendedCompany.value
    })
  }

  const proxiedImage = (url) => {
    if (!url) return ''
    return `${API_BASE_URL}/image-proxy?url=${encodeURIComponent(url)}`
  }

  // Select store with pre-auth token
  const selectStoreWithPreAuth = async (storeId) => {
    try {
      isPeak.value = false
      localStorage.removeItem('isPeak')
      // Get the pre-auth token from storage
      const preAuthTokenFromStorage = localStorage.getItem('preAuthToken')
      
      if (!preAuthTokenFromStorage) {
        console.error('No pre-auth token found')
        throw new Error('No pre-authentication token found')
      }
      
      console.log('Selecting store with pre-auth token:', preAuthTokenFromStorage.substring(0, 20) + '...')
      
      // Use the selectStoreWithToken method from authService
      const response = await authService.selectStoreWithToken(storeId, preAuthTokenFromStorage)
      
      console.log('Select store response:', response.data)
      
      if (response.data.success) {
        const { 
          access_token, 
          subcode, 
          customer_id, 
          roles,
          permissions: responsePermissions,
          primary_role,
          first_name,
          last_name,
          email,
          // eslint-disable-next-line no-unused-vars
          companies: responseCompanies, // Used for debugging but not needed in this flow
          selected_company
        } = response.data
        
        // Update token with the new token from response
        token.value = access_token
        localStorage.setItem('token', token.value)
        
        // Update user object
        const userU = {
          id: customer_id,
          customerId: customer_id,
          phone: user.value?.phone || '',
          name: first_name && last_name 
            ? `${first_name} ${last_name}`
            : first_name || last_name || '',
          firstName: first_name || '',
          lastName: last_name || '',
          email: email || '',
          isVerified: true,
          subCodeId: subcode,
          roles: roles || [],
          primaryRole: primary_role || (roles && roles[0]) || 'customer'
        }

        user.value = userU
        localStorage.setItem('user', JSON.stringify(userU))
        
        selectedRole.value = null
        localStorage.removeItem('selectedRole')
        
        // Update selected company with db_accessible flag
        if (selected_company) {
          const companyWithAccess = {
            ...selected_company,
            db_accessible: selected_company.db_accessible !== undefined 
              ? selected_company.db_accessible 
              : true
          } 
          selectedCompany.value = companyWithAccess
          localStorage.setItem('selectedCompany', JSON.stringify(companyWithAccess))
          console.log('Selected company db_accessible:', companyWithAccess.db_accessible)
        }
        
        // Update permissions
        permissions.value = responsePermissions || []
        localStorage.setItem('permissions', JSON.stringify(permissions.value))
        
        // Clear intended company if this was an auto-select
        if (intendedCompany.value) {
          intendedCompany.value = null
          localStorage.removeItem('intendedCompany')
        }
        
        // Check if database is accessible and redirect accordingly
        const isDbAccessible = selected_company?.db_accessible !== false
        const isViewer = roles.includes('VIEWER')
        
        if (isDbAccessible || isViewer) {
          router.push('/select-role')
        } else {
          // Database not accessible - go to company landing page
          console.log('Database not accessible, redirecting to company landing page')
          router.push('/company/' + encodeURIComponent(selected_company.company_name))
        }
        
        return {
          success: true,
          data: response.data
        }
      } else {
        throw new Error(response.data.message || 'Store selection failed')
      }
    } catch (error) {
      console.error('Store selection failed:', error)
      
      // Handle specific error cases
      if (error.response?.status === 403) {
        alert('Access denied to this store. Please contact support.')
      } else if (error.response?.status === 404) {
        alert('Store not found.')
      } else if (error.response?.status === 400) {
        alert('Invalid token. Please login again.')
        await logout()
        router.push('/login')
      } else {
        alert(error.response?.data?.detail || 'Error selecting store')
      }
      
      throw error
    }
  }

  const SetAccountantOperatorAndCustomerId = (operatorId, customerId) => {
    localStorage.setItem('accountant_operator_id', operatorId)
    localStorage.setItem('accountant_customer_id', customerId)
  }

  const ClearAccountantOperatorAndCustomerId = () => {
    localStorage.removeItem('accountant_customer_id')
    localStorage.removeItem('accountant_operator_id')
  }

  const GetAccountantOperatorId = () => {
    return localStorage.getItem('accountant_operator_id', null)
  }

  const GetAccountantCustomerId = () => {
    return localStorage.getItem('accountant_customer_id', null)
  }
  
  // Call initialization
  initializeFromStorage()

  return {
    // State
    user,
    token,
    selectedRole,
    companies,
    selectedCompany,
    redirectPath,
    intendedCompany,  
    permissions,
    
    // Operator states
    isOperator,
    operatorData,
    checkingOperator,
    operatorCheckComplete,
    
    // Computed
    isAuthenticated,
    getCurrentRole,
    hasMultipleRoles,
    getAvailableRoles,
    hasMultipleCompanies,
    hasCompanySelected,
    getCompaniesWithDbAccess,
    getFirstAccessibleCompany,
    getSubcode,
    getCustomerId,
    getSelectedCompanyId,
    needsCompanySelection,
    
    // Methods
    login,
    selectCompany,
    logout,
    setSelectedRole,
    setSelectedCompany,
    hasRole,
    setPasswordAndLogin,
    checkAuth,
    checkOperatorStatus,
    getRoleDashboard,
    handleIntendedCompany,
    setIntendedCompany,
    hasCompanyAccess,
    getCompanyById,
    getPreAuthToken,
    switchCompany,
    switchStore,
    setIsPeak,
    proxiedImage,
    selectStoreWithPreAuth, // Export the new method
    clearRedirectPath: () => {
      redirectPath.value = null
    },
    fetchCompanyDatabases,
    companyDatabases,
    activeDatabase,
    switchDatabase,
    SetAccountantOperatorAndCustomerId,
    ClearAccountantOperatorAndCustomerId,
    GetAccountantCustomerId,
    GetAccountantOperatorId
  }
})
